import { supabase } from '../supabase'
import {
  decodeEpc,
  getSchemeWordCount,
  pcWordForWordCount,
  wordCountFromPcWord,
} from '../../utils/epcComposer'
import type { RfidEpcScheme } from '../../types/rfid'
import { EncoderError, type UhfEncoderClient } from '../rfid/uhf-encoder-client'

/**
 * Encode orchestration for the desktop RFID encoder.
 *
 * The reader's InventoryG2 does not return results on this hardware, so tag
 * presence is proven by the TID read instead. Everything protective therefore
 * hangs off the pre-read: TID, then the PC word (which declares the tag's own
 * EPC length), then the EPC at that declared length.
 */

const PENDING_GRACE_MS = 5 * 60 * 1000
const MAX_TAG_WORDS = 31

export type EncodeBlockReason =
  | 'tid_encoded'
  | 'tid_quarantined'
  | 'tid_pending_stale'
  | 'tid_pending_active'
  | 'epc_known'
  | 'epc_scheme_no_record'

export interface RfidUnitRecord {
  id: string
  epc_full: string
  epc_base: string
  serial: string
  tid: string | null
  status: 'PENDING' | 'ENCODED' | 'QUARANTINED' | 'SUPERSEDED'
  product_name: string | null
  color_name: string | null
  size_name: string | null
  sku: string | null
  encoded_at: string | null
  created_at: string
}

export interface TagPreRead {
  tid: string
  pcWord: string
  declaredWords: number
  epc: string
}

export type EncodeClassification =
  | { outcome: 'proceed'; preRead: TagPreRead }
  | {
      outcome: 'blocked'
      preRead: TagPreRead
      reason: EncodeBlockReason
      /** Null for case 5: a scheme EPC with no record has nothing to retire. */
      existingRecord: RfidUnitRecord | null
      /** A hard block cannot be resolved by confirming an overwrite. */
      canOverwrite: boolean
      title: string
      detail: string
    }

export interface EncodeContext {
  adminId: string
  userId: string | null
  scheme: RfidEpcScheme
  client: UhfEncoderClient
  productId: string
  epcBase: string
  sku: string | null
  barcode: string | null
  productName: string | null
  colorName: string | null
  sizeName: string | null
  deviceSerial: string | null
  rfPower: number | null
}

export interface EncodeVerification {
  tidMatches: boolean
  pcMatches: boolean
  epcMatches: boolean
  readBackEpc: string | null
  readBackPc: string | null
  readBackTid: string | null
}

export interface EncodeSuccess {
  outcome: 'encoded'
  recordId: string
  epcFull: string
  serial: string
  verification: EncodeVerification
  supersededRecordId: string | null
}

export interface EncodeFailure {
  outcome: 'quarantined'
  recordId: string
  epcFull: string
  serial: string
  verification: EncodeVerification
  error: string
}

export type EncodeResult = EncodeSuccess | EncodeFailure

// ---------------------------------------------------------------------------
// Pre-read
// ---------------------------------------------------------------------------

/**
 * Read the tag at the length the TAG declares, not the length our scheme wants.
 * A tag from other stock may declare 8 words; reading 6 from it would return a
 * truncated EPC and classify it wrongly.
 */
export async function preReadTag(client: UhfEncoderClient): Promise<TagPreRead> {
  const tid = await client.readTid()
  const pcWord = await client.readPc()
  const declaredWords = wordCountFromPcWord(pcWord)

  if (!declaredWords || declaredWords > MAX_TAG_WORDS) {
    throw new EncoderError(
      'read_failed',
      `Tag reported an unusable EPC length (PC word ${pcWord}). Encoding aborted.`
    )
  }

  const epc = await client.readEpc(declaredWords)
  if (!epc)
    throw new EncoderError('read_failed', 'Could not read the current EPC. Encoding aborted.')

  return { tid, pcWord, declaredWords, epc }
}

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

function describeUnit(record: RfidUnitRecord): string {
  const parts = [record.product_name, record.color_name, record.size_name].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : record.sku || 'Unknown item'
}

async function findByTid(adminId: string, tid: string): Promise<RfidUnitRecord | null> {
  const { data, error } = await supabase
    .from('rfid_encoded_units')
    .select(
      'id, epc_full, epc_base, serial, tid, status, product_name, color_name, size_name, sku, encoded_at, created_at'
    )
    .eq('admin_id', adminId)
    .eq('tid', tid)
    .neq('status', 'SUPERSEDED')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw new Error(error.message || 'Failed to look up tag by TID')
  return (data as RfidUnitRecord | null) ?? null
}

async function findByEpc(adminId: string, epc: string): Promise<RfidUnitRecord | null> {
  const { data, error } = await supabase
    .from('rfid_encoded_units')
    .select(
      'id, epc_full, epc_base, serial, tid, status, product_name, color_name, size_name, sku, encoded_at, created_at'
    )
    .eq('admin_id', adminId)
    .eq('epc_full', epc)
    .maybeSingle()

  if (error) throw new Error(error.message || 'Failed to look up tag by EPC')
  return (data as RfidUnitRecord | null) ?? null
}

/**
 * Decided from OUR database, never from what the tag happens to hold. Every
 * fresh label carries a factory EPC, so treating "not blank" as suspicious
 * would fire on all of them and train operators to click through the prompt
 * that actually matters.
 *
 * Cases are evaluated in order and the first match wins: TID (the immutable
 * physical identity) outranks EPC (mutable content).
 */
export async function classifyTag(params: {
  adminId: string
  scheme: RfidEpcScheme
  preRead: TagPreRead
}): Promise<EncodeClassification> {
  const { adminId, scheme, preRead } = params

  const byTid = await findByTid(adminId, preRead.tid)

  if (byTid?.status === 'ENCODED') {
    return {
      outcome: 'blocked',
      preRead,
      reason: 'tid_encoded',
      existingRecord: byTid,
      canOverwrite: true,
      title: 'RFID tag already encoded',
      detail: `This chip is registered as ${describeUnit(byTid)} (unit ${byTid.serial}).`,
    }
  }

  if (byTid?.status === 'QUARANTINED') {
    return {
      outcome: 'blocked',
      preRead,
      reason: 'tid_quarantined',
      existingRecord: byTid,
      canOverwrite: true,
      title: 'RFID tag is quarantined',
      detail: `A previous encode of ${describeUnit(byTid)} failed verification, so this chip's contents are unknown.`,
    }
  }

  if (byTid?.status === 'PENDING') {
    const age = Date.now() - new Date(byTid.created_at).getTime()
    if (age < PENDING_GRACE_MS) {
      return {
        outcome: 'blocked',
        preRead,
        reason: 'tid_pending_active',
        existingRecord: byTid,
        canOverwrite: false,
        title: 'Encode in progress on this tag',
        detail:
          'A write for this chip was started moments ago and has not been confirmed. Wait for it to finish before encoding again.',
      }
    }

    return {
      outcome: 'blocked',
      preRead,
      reason: 'tid_pending_stale',
      existingRecord: byTid,
      canOverwrite: true,
      title: 'Incomplete encode on this tag',
      detail: `An encode of ${describeUnit(byTid)} (unit ${byTid.serial}) was never confirmed, so this chip's contents are unknown.`,
    }
  }

  const byEpc = await findByEpc(adminId, preRead.epc)
  if (byEpc) {
    return {
      outcome: 'blocked',
      preRead,
      reason: 'epc_known',
      existingRecord: byEpc,
      canOverwrite: true,
      title: 'This EPC is already registered',
      detail: `The EPC on this tag belongs to ${describeUnit(byEpc)} (unit ${byEpc.serial}).`,
    }
  }

  // Discriminate on the scheme's version segment, never a hardcoded factory
  // prefix: factory EPCs are not guaranteed to start with any fixed value.
  if (preRead.epc.length === scheme.total_length) {
    const decoded = decodeEpc(scheme, preRead.epc)
    if (decoded.isValid && decoded.fields.versionCode === scheme.version_code) {
      return {
        outcome: 'blocked',
        preRead,
        reason: 'epc_scheme_no_record',
        existingRecord: null,
        canOverwrite: true,
        title: 'Tag carries one of our EPCs with no record',
        detail:
          'This EPC matches the active scheme but has no unit record. It was encoded outside the system, or its record was lost.',
      }
    }
  }

  return { outcome: 'proceed', preRead }
}

// ---------------------------------------------------------------------------
// Encode
// ---------------------------------------------------------------------------

interface ReserveRow {
  out_record_id: string
  out_serial: string
  out_epc_full: string
}

async function reserveSerial(
  context: EncodeContext,
  tid: string
): Promise<ReserveRow> {
  const { data, error } = await supabase.rpc('reserve_rfid_serial', {
    p_admin_id: context.adminId,
    p_product_id: context.productId,
    p_epc_base: context.epcBase,
    p_serial_length: context.scheme.serial_length,
    p_tid: tid,
    p_scheme_id: context.scheme.id,
    p_scheme_version: context.scheme.version_code,
    p_device_serial: context.deviceSerial,
    p_rf_power: context.rfPower,
    p_context: {
      sku: context.sku,
      barcode: context.barcode,
      product_name: context.productName,
      color_name: context.colorName,
      size_name: context.sizeName,
    },
  })

  if (error) throw new Error(error.message || 'Failed to reserve an RFID serial')

  const row = Array.isArray(data) ? (data[0] as ReserveRow | undefined) : (data as ReserveRow)
  if (!row?.out_record_id) throw new Error('Serial reservation returned no record')
  return row
}

async function finalize(params: {
  context: EncodeContext
  recordId: string
  status: 'ENCODED' | 'QUARANTINED'
  pcWord: string | null
  verifiedTid: string | null
  errorDetail: string | null
  supersededRecordId: string | null
  overwroteEpc: string | null
}): Promise<void> {
  const { error } = await supabase.rpc('finalize_rfid_encode', {
    p_admin_id: params.context.adminId,
    p_record_id: params.recordId,
    p_status: params.status,
    p_pc_word: params.pcWord,
    p_verified_tid: params.verifiedTid,
    p_error_detail: params.errorDetail,
    p_encoded_by: params.context.userId,
    p_superseded_record_id: params.supersededRecordId,
    p_overwrote_epc: params.overwroteEpc,
  })

  if (error) throw new Error(error.message || 'Failed to finalize the RFID encode')
}

/**
 * Reserve, write, and verify four ways. Anything short of all four passing
 * leaves the attempt QUARANTINED with its serial burned — a gap in the sequence
 * is the audit trail, so serials are never reissued.
 */
export async function encodeTag(params: {
  context: EncodeContext
  preRead: TagPreRead
  /** Set only after an explicit per-tag overwrite confirmation. */
  supersedeRecord?: RfidUnitRecord | null
  overwroteEpc?: string | null
}): Promise<EncodeResult> {
  const { context, preRead } = params
  const words = getSchemeWordCount(context.scheme)
  const expectedPc = pcWordForWordCount(words)

  const reserved = await reserveSerial(context, preRead.tid)
  const epcFull = reserved.out_epc_full

  const verification: EncodeVerification = {
    tidMatches: false,
    pcMatches: false,
    epcMatches: false,
    readBackEpc: null,
    readBackPc: null,
    readBackTid: null,
  }

  let failure: string | null = null

  try {
    await context.client.writeEpc(epcFull, words)

    verification.readBackTid = await context.client.readTid()
    verification.tidMatches = verification.readBackTid === preRead.tid
    if (!verification.tidMatches)
      failure = `A different tag answered after the write (TID ${verification.readBackTid} instead of ${preRead.tid}).`

    verification.readBackPc = await context.client.readPc()
    verification.pcMatches = verification.readBackPc.toUpperCase() === expectedPc
    if (!failure && !verification.pcMatches)
      failure = `Tag reports PC word ${verification.readBackPc} instead of ${expectedPc}.`

    verification.readBackEpc = await context.client.readEpc(words)
    verification.epcMatches = verification.readBackEpc.toUpperCase() === epcFull.toUpperCase()
    if (!failure && !verification.epcMatches)
      failure = `Tag reads back ${verification.readBackEpc} instead of ${epcFull}.`
  } catch (err) {
    failure = err instanceof Error ? err.message : 'The encoder failed during the write'
  }

  const isVerified =
    !failure && verification.tidMatches && verification.pcMatches && verification.epcMatches

  if (!isVerified) {
    await finalize({
      context,
      recordId: reserved.out_record_id,
      status: 'QUARANTINED',
      pcWord: verification.readBackPc,
      verifiedTid: preRead.tid,
      errorDetail: failure,
      supersededRecordId: null,
      overwroteEpc: null,
    })

    return {
      outcome: 'quarantined',
      recordId: reserved.out_record_id,
      epcFull,
      serial: reserved.out_serial,
      verification,
      error: failure || 'Verification failed',
    }
  }

  const supersedeRecord = params.supersedeRecord ?? null
  const divergence =
    supersedeRecord && supersedeRecord.tid && supersedeRecord.tid !== preRead.tid
      ? `Retired record matched by EPC; its TID (${supersedeRecord.tid}) differs from the encoded chip (${preRead.tid}).`
      : null

  await finalize({
    context,
    recordId: reserved.out_record_id,
    status: 'ENCODED',
    pcWord: verification.readBackPc,
    verifiedTid: preRead.tid,
    errorDetail: divergence,
    supersededRecordId: supersedeRecord?.id ?? null,
    overwroteEpc: params.overwroteEpc ?? supersedeRecord?.epc_full ?? null,
  })

  return {
    outcome: 'encoded',
    recordId: reserved.out_record_id,
    epcFull,
    serial: reserved.out_serial,
    verification,
    supersededRecordId: supersedeRecord?.id ?? null,
  }
}

// ---------------------------------------------------------------------------
// Stale attempts
// ---------------------------------------------------------------------------

export async function listStalePendingEncodes(adminId: string): Promise<RfidUnitRecord[]> {
  const cutoff = new Date(Date.now() - PENDING_GRACE_MS).toISOString()

  const { data, error } = await supabase
    .from('rfid_encoded_units')
    .select(
      'id, epc_full, epc_base, serial, tid, status, product_name, color_name, size_name, sku, encoded_at, created_at'
    )
    .eq('admin_id', adminId)
    .eq('status', 'PENDING')
    .lt('created_at', cutoff)
    .order('created_at', { ascending: false })
    .limit(25)

  if (error) throw new Error(error.message || 'Failed to load incomplete encode attempts')
  return (data || []) as RfidUnitRecord[]
}
